import React, {
  forwardRef,
  ForwardRefExoticComponent,
  HTMLAttributes,
  HTMLProps,
  ReactElement,
  RefAttributes,
  RefObject,
} from 'react';
import styled, { StyledComponent } from 'styled-components';
import { Popover, PopoverProps, ArrowContainer } from 'react-tiny-popover';
import colors from '../common/theme/colors';
import constants from '../common/theme/constants';

export { Popover } from 'react-tiny-popover';
export type { PopoverProps } from 'react-tiny-popover';

/**
 KitPopover uses react-tiny-popover:
 https://github.com/alexkatz/react-tiny-popover

 For info on available properties, please look at react-tiny-popover docs.
*/

const Trigger = styled.div`
  display: inline-block;
`;

enum PopoverRoles {
  Dialog = 'dialog',
  Tooltip = 'tooltip',
}

export interface KitPopoverProps extends PopoverProps {
  role?: keyof PopoverRoles;
  isOpen: boolean;
  children: ReactElement;
}

type KitPopoverDef = React.FC<KitPopoverProps> & {
  Trigger: ForwardRefExoticComponent<HTMLProps<HTMLDivElement> & RefAttributes<HTMLElement>>;
  ArrowContainer: typeof ArrowContainer;
  Container: StyledComponent<'div', HTMLAttributes<HTMLDivElement>>;
};

const KitPopover: KitPopoverDef = (props: KitPopoverProps) => {
  const { children /* role = Roles.Dialog */ } = props;
  const defaults: Partial<PopoverProps> = {
    padding: constants.spacing.absolute.xs,
    positions: ['bottom'],
    align: 'center',
    containerStyle: { zIndex: '1' },
  };
  const normalizedProps: PopoverProps = Object.keys(props).reduce((acc, key: string) => {
    acc[key] = props[key];
    return acc;
  }, defaults) as PopoverProps;

  return (
    <Popover aria-modal={false} aria-hidden={props.isOpen} {...normalizedProps}>
      {children}
    </Popover>
  );
};

KitPopover.Trigger = forwardRef(
  (props: HTMLProps<HTMLDivElement>, ref: RefObject<HTMLDivElement>) => (
    <Trigger ref={ref as React.RefObject<HTMLDivElement>}>{props.children}</Trigger>
  )
);

// These containers are provided for convenience, but you can use any container you want to hold the Popover content

KitPopover.ArrowContainer = ArrowContainer;
KitPopover.Container = styled.div`
  background: ${colors.white};
  box-shadow: 0 3px 2px -2px rgba(0, 0, 0, 0.2), 0 2px 2px 0 rgba(0, 0, 0, 0.14) !important;
`;

export default KitPopover;
